
import {
  computed, defineComponent, PropType,
} from 'vue';
import Dialog from 'primevue/dialog';

export default defineComponent({
  name: 'WizardDialog',

  components: { Dialog },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    phase: {
      type: String,
      required: true,
    },
    phases: {
      type: Array as PropType<string[]>,
      required: true,
    },
    titles: {
      type: Object as PropType<Record<string, string>>,
      default: () => ({}),
    },
    subtitles: {
      type: Object as PropType<Record<string, string>>,
      default: () => ({}),
    },
    locked: {
      type: Boolean,
      default: false,
    },
    width: {
      type: String,
      default: '960px',
    },
    closeLabel: {
      type: String,
      default: 'Close',
    },
    /**
     * When true, the dialog fills the entire viewport (100vw × 100vh, no
     * rounded corners, no margin). Used for heavy multi-phase flows like
     * ChangePlanDialog where horizontal space is precious.
     */
    fullScreen: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update:visible', 'update:phase', 'close'],

  setup(props, { emit }) {
    const currentIndex = computed(() => {
      const i = props.phases.indexOf(props.phase);
      return i < 0 ? 0 : i;
    });

    // Class applied directly on the <Dialog> root via `v-bind="$attrs"` —
    // PrimeVue 3.23 does NOT support the :pt API (introduced in later
    // versions), so we cannot target the mask through a pt key. We rely on
    // :class on the dialog + :has() on the mask in the CSS below.
    const rootClass = computed(() => (
      props.fullScreen
        ? 'wizard-dialog-root wizard-dialog-root--full'
        : 'wizard-dialog-root'
    ));

    const dialogStyle = computed(() => (
      props.fullScreen
        ? { width: '100vw', maxWidth: '100vw', height: '100vh', maxHeight: '100vh' }
        : { width: props.width, maxWidth: '95vw' }
    ));

    const currentTitle = computed(() => props.titles[props.phase] ?? '');
    const currentSubtitle = computed(() => props.subtitles[props.phase] ?? '');

    const progressWidth = computed(() => {
      const ratio = (currentIndex.value + 1) / props.phases.length;
      return `${Math.round(ratio * 100)}%`;
    });

    const onVisibleChange = (v: boolean) => {
      if (!v) onClose();
    };

    const onClose = () => {
      if (props.locked) return;
      emit('update:visible', false);
      emit('close');
    };

    return {
      currentTitle,
      currentSubtitle,
      progressWidth,
      rootClass,
      dialogStyle,
      onVisibleChange,
      onClose,
    };
  },
});
